import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Chip, CircularProgress, Grid, MenuItem, Tab, Tabs, TextField, Typography
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useHistory, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { SearchService } from '../../services/dao/search-service';
import { ProjectsService } from '../../services/dao/projects-service';

const searchService = new SearchService();
const projectsService = new ProjectsService();
const fmtMoney = (v) => v != null ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
const fmtDate = (d) => { try { return d ? format(new Date(d), 'MMM d, yyyy') : null; } catch { return null; } };

function highlightMatch(text, query) {
    if (!text || !query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return <>{text.slice(0, idx)}<mark style={{ background: '#FEF08A', borderRadius: 2, padding: '0 2px' }}>{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>;
}

function FindocResult({ findoc, matchingItems, query }) {
    const history = useHistory();
    const title = findoc.institutionName || 'Statement';
    const date = fmtDate(findoc.statementDate);

    return (
        <Box sx={{ borderRadius: 2.5, background: '#fff', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {/* Parent doc header */}
            <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', cursor: 'pointer',
                borderBottom: matchingItems.length > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                '&:hover': { background: '#F8FAFC' } }}
                onClick={() => history.push(`/findocs/${findoc._id}`)}>
                <DescriptionIcon sx={{ color: '#1F2F42', fontSize: '1.2rem', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{title}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {date}{findoc.accountId ? ` · ${findoc.accountId}` : ''}
                    </Typography>
                </Box>
                {findoc.balance != null && (
                    <Chip label={fmtMoney(findoc.balance)} size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', background: '#E8EDF2', color: '#141F2E' }} />
                )}
            </Box>
            {/* Matching line items */}
            {matchingItems.map((li, i) => (
                <Box key={i} sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: i < matchingItems.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
                    cursor: 'pointer', '&:hover': { background: '#FAFAFA' } }}
                    onClick={() => history.push(`/findocs/${findoc._id}`)}>
                    <Box sx={{ flex: 1, minWidth: 0, pl: 4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            {highlightMatch(li.description, query)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                            {fmtDate(li.transactionDate) && (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmtDate(li.transactionDate)}</Typography>
                            )}
                            {li.category && (
                                <Chip label={li.category} size="small" sx={{ fontSize: '0.65rem', height: 18, fontWeight: 600, background: '#F1F5F9', color: '#475569' }} />
                            )}
                        </Box>
                    </Box>
                    {li.amount != null && (
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', flexShrink: 0,
                            color: li.transactionType === 'debit' ? '#DC2626' : li.transactionType === 'credit' ? '#059669' : (li.amount > 0 ? '#DC2626' : '#059669') }}>
                            {fmtMoney(li.amount)}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function ReceiptResult({ receipt, matchingItems, query }) {
    const history = useHistory();
    const title = receipt.vendorName || 'Receipt';
    const date = fmtDate(receipt.invoiceReceiptDate);

    return (
        <Box sx={{ borderRadius: 2.5, background: '#fff', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', cursor: 'pointer',
                borderBottom: matchingItems.length > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                '&:hover': { background: '#F8FAFC' } }}
                onClick={() => history.push(`/receipts/${receipt._id}`)}>
                <ReceiptLongIcon sx={{ color: '#C89E57', fontSize: '1.2rem', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{highlightMatch(title, query)}</Typography>
                    {date && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{date}</Typography>}
                </Box>
                {receipt.total != null && (
                    <Chip label={fmtMoney(receipt.total)} size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', background: '#ECFDF5', color: '#059669' }} />
                )}
            </Box>
            {matchingItems.map((li, i) => (
                <Box key={i} sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: i < matchingItems.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
                    cursor: 'pointer', '&:hover': { background: '#FAFAFA' } }}
                    onClick={() => history.push(`/receipts/${receipt._id}`)}>
                    <Box sx={{ flex: 1, minWidth: 0, pl: 4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            {highlightMatch(li.item, query)}
                        </Typography>
                    </Box>
                    {li.price != null && (
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', flexShrink: 0 }}>
                            {fmtMoney(li.price)}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

export default function SearchScreen() {
    const location = useLocation();
    const history = useHistory();
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    const projectIdParam = params.get('projectId') || '';
    const [findocs, setFindocs] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);
    const [searched, setSearched] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(projectIdParam);

    useEffect(() => {
        projectsService.getProjects().then(r => {
            if (r?.body) setProjects(r.body.filter(p => p._id));
        });
    }, []);

    useEffect(() => {
        if (!query.trim()) { setFindocs([]); setReceipts([]); setSearched(false); return; }
        setLoading(true);
        searchService.search(query, selectedProject || undefined).then(r => {
            setFindocs(r?.body?.findocs || []);
            setReceipts(r?.body?.receipts || []);
            setSearched(true);
            setLoading(false);
        });
    }, [query, selectedProject]);

    const handleProjectChange = (e) => {
        const val = e.target.value;
        setSelectedProject(val);
        const p = new URLSearchParams(location.search);
        if (val) p.set('projectId', val); else p.delete('projectId');
        history.replace('/search?' + p.toString());
    };

    // Filter line items that match the query
    const q = query.toLowerCase();
    const findocResults = useMemo(() => findocs.map(f => ({
        doc: f,
        matchingItems: (f.lineItems || []).filter(li =>
            (li.description || '').toLowerCase().includes(q) ||
            (li.category || '').toLowerCase().includes(q)
        )
    })), [findocs, q]);

    const receiptResults = useMemo(() => receipts.map(r => ({
        doc: r,
        matchingItems: (r.lineItems || []).filter(li =>
            (li.item || '').toLowerCase().includes(q)
        )
    })), [receipts, q]);

    const totalLineItems = findocResults.reduce((s, r) => s + r.matchingItems.length, 0)
        + receiptResults.reduce((s, r) => s + r.matchingItems.length, 0);
    const total = findocs.length + receipts.length;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Search Results</Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {searched ? `${total} document${total !== 1 ? 's' : ''}, ${totalLineItems} line item${totalLineItems !== 1 ? 's' : ''} for "${query}"` : 'Searching...'}
                </Typography>
                <TextField select size="small" value={selectedProject} onChange={handleProjectChange}
                    sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
                    label="Filter by project">
                    <MenuItem value="">All Projects</MenuItem>
                    <MenuItem value="unassigned">Unassigned</MenuItem>
                    {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
                </TextField>
            </Box>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>}

            {!loading && searched && total === 0 && (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <SearchOffIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>No results found</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term or project</Typography>
                </Box>
            )}

            {!loading && total > 0 && (
                <>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' } }}>
                        <Tab label={`Statements (${findocs.length})`} icon={<DescriptionIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                        <Tab label={`Receipts (${receipts.length})`} icon={<ReceiptLongIcon sx={{ fontSize: '1.1rem' }} />} iconPosition="start" />
                    </Tabs>

                    {tab === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {findocResults.map(({ doc, matchingItems }) => (
                                <FindocResult key={doc._id} findoc={doc} matchingItems={matchingItems} query={query} />
                            ))}
                            {findocs.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>No matching statements</Typography>}
                        </Box>
                    )}
                    {tab === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {receiptResults.map(({ doc, matchingItems }) => (
                                <ReceiptResult key={doc._id} receipt={doc} matchingItems={matchingItems} query={query} />
                            ))}
                            {receipts.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>No matching receipts</Typography>}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
